import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  BOARD_REFUSED,
  BOARD_UNSET,
  embedBoard,
  LOST_SERVER,
  MAT_MISSING,
  matStatusText,
  parseMatPoll,
  POLL_INTERVAL_MS,
  pollPath,
  showLostServer,
  showReconnect,
  type MatPoll,
} from '../mat-poll';

type View = { kind: 'loading' } | { kind: 'missing' } | { kind: 'offline' } | { kind: 'ready'; poll: MatPoll };

/** A new slug or mat number remounts the screen so the previous board cannot linger. */
export function MatDisplayPage() {
  const params = useParams();
  const slug = params.slug ?? '';
  const number = params.number ?? '';
  return <MatScreen key={`${slug}/${number}`} slug={slug} number={number} />;
}

/**
 * Bookmarked mat screen. The poll is anonymous: the session cookie is not sent,
 * so this page cannot slide a head-table session. The iframe is not sandboxed
 * because the board's own scripts have to keep refreshing (ADR-004).
 * An allow-listed URL stays in that iframe. Page script sees the same signals
 * for a browser-refused frame and a board that loaded, so it does not try to
 * tell them apart (ADR-004, amended 2026-10-12).
 */
function MatScreen({ slug, number }: { slug: string; number: string }) {
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [lastSuccess, setLastSuccess] = useState<number | null>(null);
  const [lastFailed, setLastFailed] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    const clock = window.setInterval(() => setNow(Date.now()), POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(clock);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function poll(): Promise<void> {
      try {
        const response = await fetch(pollPath(slug, number), {
          method: 'GET',
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
          headers: { accept: 'application/json' },
        });
        if (cancelled) return;
        if (response.status === 404) {
          setView({ kind: 'missing' });
          setLastSuccess(null);
          setLastFailed(false);
          return;
        }
        if (!response.ok) {
          setLastFailed(true);
          setView((current) => (current.kind === 'ready' ? current : { kind: 'offline' }));
          return;
        }
        const parsed = parseMatPoll(await response.json());
        if (cancelled) return;
        if (!parsed) {
          setLastFailed(true);
          setView((current) => (current.kind === 'ready' ? current : { kind: 'offline' }));
          return;
        }
        setLastFailed(false);
        setLastSuccess(Date.now());
        setView((current) => {
          if (current.kind === 'ready' && samePoll(current.poll, parsed)) return current;
          return { kind: 'ready', poll: parsed };
        });
      } catch {
        if (cancelled) return;
        setLastFailed(true);
        setView((current) => (current.kind === 'ready' ? current : { kind: 'offline' }));
      }
    }

    void poll();
    const timer = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [slug, number]);

  const embed = view.kind === 'ready' ? embedBoard(view.poll.bracket_url) : null;
  const stale = showReconnect(lastSuccess, now);
  const lost = showLostServer({
    hidden,
    phase: view.kind,
    lastFailed,
    lastSuccessAt: lastSuccess,
    now,
    startedAt,
  });
  const status = view.kind === 'ready' ? matStatusText(stale, lost) : null;
  const refused = embed?.kind === 'refused';
  const refusedHost = embed?.kind === 'refused' ? embed.host : null;

  return (
    <main className="mat-screen">
      <section className="mat-frame" aria-label="Mat display">
        {view.kind === 'ready' && embed?.kind === 'embed' ? (
          <h1 className="mat-label">
            <span className="kicker">{view.poll.tournament_name}</span>
            {view.poll.mat_name}
          </h1>
        ) : null}
        {view.kind === 'ready' && embed?.kind === 'embed' ? (
          <iframe className="mat-board" title={`${view.poll.mat_name} bracket board`} src={embed.url} />
        ) : null}
        {view.kind === 'ready' && embed?.kind === 'empty' ? (
          <div className="mat-message">
            <p className="kicker">{view.poll.tournament_name}</p>
            <h1>{view.poll.mat_name}</h1>
            <p>{BOARD_UNSET}</p>
          </div>
        ) : null}
        {view.kind === 'ready' && refused ? (
          <div className="mat-message">
            <p className="kicker">{view.poll.tournament_name}</p>
            <h1>{view.poll.mat_name}</h1>
            <p>{BOARD_REFUSED}</p>
            {refusedHost ? <p>{refusedHost}</p> : null}
          </div>
        ) : null}
        {view.kind === 'missing' ? (
          <div className="mat-message">
            <p className="kicker">Mat Monitor</p>
            <h1>Mat not found</h1>
            <p>{MAT_MISSING}</p>
          </div>
        ) : null}
        {view.kind !== 'ready' && view.kind !== 'missing' && lost ? (
          <div className="mat-message">
            <p className="kicker">Mat Monitor</p>
            <h1>Mat Monitor</h1>
            <p role="status">{LOST_SERVER}</p>
          </div>
        ) : null}
        {view.kind === 'loading' && !lost ? <p className="kicker">Mat Monitor</p> : null}
        {status ? (
          <p className="mat-reconnect" role="status">
            {status}
          </p>
        ) : null}
      </section>
    </main>
  );
}

function samePoll(left: MatPoll, right: MatPoll): boolean {
  return (
    left.tournament_name === right.tournament_name &&
    left.mat_number === right.mat_number &&
    left.mat_name === right.mat_name &&
    left.bracket_url === right.bracket_url &&
    left.remote_muted === right.remote_muted &&
    left.notification === right.notification
  );
}
